import { IBriefPricingResponse } from "@shared/lib/types/pricingWorkload";
import { CircularProgress } from "@shared/ui";
import { Calculator } from "lucide-react";
import styles from "../styles.module.scss";

interface PricingStatusSummaryProps {
  data?: IBriefPricingResponse;
  isLoading: boolean;
}

type StatBoxTone = "default" | "progress" | "muted";

interface StatBoxProps {
  label: string;
  value: number | string;
  sub?: string;
  subLabel?: string;
  valueIsText?: boolean;
  tone?: StatBoxTone;
}

function StatBox({
  label,
  value,
  sub,
  subLabel,
  valueIsText,
  tone = "default",
}: StatBoxProps) {
  const boxClass = [
    styles.statBox,
    tone === "progress" ? styles.statBoxProgress : "",
    tone === "muted" ? styles.statBoxMuted : "",
  ]
    .filter(Boolean)
    .join(" ");

  const valueClass = [
    valueIsText ? styles.statValueText : styles.statValue,
    tone === "progress" ? styles.statValueProgress : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={boxClass}>
      <div className={styles.statLabel}>{label}</div>
      {subLabel ? <div className={styles.statSubLabel}>{subLabel}</div> : null}
      <div className={valueClass}>{value}</div>
      {sub ? <div className={styles.statSub}>{sub}</div> : null}
    </div>
  );
}

export function PricingStatusSummary({
  data,
  isLoading,
}: PricingStatusSummaryProps) {
  const planTotal = data?.total ?? 0;
  const allocatedOnQueue = data?.allocated ?? 0;
  const pricedOnQueue = data?.allocatedPriced ?? 0;
  const leftToAllocate = data?.leftToAllocated ?? 0;
  const noContact = data?.isCreatorOnboardPending ?? 0;

  const pricedAllocatedSub =
    allocatedOnQueue > 0
      ? `${pricedOnQueue} priced of ${allocatedOnQueue} on queue`
      : "No one on queue";

  return (
    <div className={styles.summaryCard}>
      <div className={styles.summaryHeader}>
        <div className={styles.summaryIcon}>
          <Calculator size={20} />
        </div>
        <div className={styles.summaryHeaderText}>
          <h3 className={styles.summaryTitle}>Campaign pricing status</h3>
          <p className={styles.summarySubtitle}>
            Creators in plan vs internal pricing queue. Use the tabs below for
            progress, reallocation, and POC workload.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loaderRow}>
          <CircularProgress />
        </div>
      ) : (
        <div className={styles.statGrid}>
          <StatBox label="In plan" value={planTotal} />
          <StatBox
            label="Priced / allocated"
            value={`${pricedOnQueue}/${allocatedOnQueue}`}
            valueIsText
            sub={pricedAllocatedSub}
            tone="progress"
          />
          <StatBox
            label="Left to allocate"
            value={leftToAllocate}
            tone={leftToAllocate > 0 ? "muted" : "default"}
          />
          <StatBox
            label="No contact"
            value={noContact}
            tone={noContact > 0 ? "muted" : "default"}
          />
        </div>
      )}
    </div>
  );
}
